import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import { isNil } from 'lodash';
import slugify from 'slugify';
import { zoneLabelAt } from './zone-label';

dayjs.extend(utc);
dayjs.extend(timezone);

export const isNilOrEmptyString = (
  obj: unknown,
): obj is null | undefined | '' => {
  return isNil(obj) || (typeof obj === 'string' && obj.length === 0);
};

export const isNotNilOrEmptyString = (
  obj: unknown,
): obj is Exclude<string, ''> => {
  return !isNil(obj) && typeof obj === 'string' && obj.length > 0;
};

/**
 * If the value is null, undefined, or '', returns undefined. Otherwise, applies
 * the provided function to the value.
 */
export const mapUndefinedOrEmptyString = <TypeA, TypeB>(
  fn: (value: TypeA) => TypeB,
  value: TypeA | null | undefined | '',
): TypeB | undefined => {
  if (isNilOrEmptyString(value)) {
    return undefined;
  }

  return fn(value);
};

/**
 * If the value is null, undefined, or '', returns null. Otherwise, applies the
 * provided function to the value.
 */
export const mapNullOrEmptyString = <TypeA, TypeB>(
  fn: (value: TypeA) => TypeB,
  value: TypeA | null | undefined | '',
): TypeB | null => {
  if (isNilOrEmptyString(value)) {
    return null;
  }

  return fn(value);
};

const DATE_TIME_STRING_FORMAT = 'MM/DD hh:mm a';

export const transformDateTimeToDateTimeString = ({
  dateTime,
  timeZone,
}: {
  dateTime: Date | null | undefined;
  /** If this is undefined, defaults to the local system time zone */
  timeZone: string | undefined;
}) => {
  if (isNil(dateTime)) {
    return '-';
  }
  if (isNilOrEmptyString(timeZone)) {
    return dayjs(dateTime).format(DATE_TIME_STRING_FORMAT);
  }
  return dayjs(dateTime).tz(timeZone).format(DATE_TIME_STRING_FORMAT);
};

/**
 * A stop time printed on an invoice (D-10): with the stop's zone (set only
 * while terminal_local_times is on) it is the stop's local time, 24-hour with
 * its label (`09/22 23:30 MST`); without one, the company-zone text as before.
 */
export const transformStopDateTimeToDateTimeString = ({
  dateTime,
  stopTimeZone,
  companyTimeZone,
}: {
  dateTime: Date | null | undefined;
  stopTimeZone: string | null | undefined;
  companyTimeZone: string | undefined;
}): string => {
  if (isNil(dateTime) || isNilOrEmptyString(stopTimeZone)) {
    return transformDateTimeToDateTimeString({
      dateTime,
      timeZone: companyTimeZone,
    });
  }
  return `${dayjs(dateTime).tz(stopTimeZone).format('MM/DD HH:mm')} ${zoneLabelAt(
    dayjs(dateTime).toDate(),
    stopTimeZone,
  )}`;
};

// Vendoring this in here because we can't import ESM modules yet.
// https://github.com/sindresorhus/filename-reserved-regex/blob/ce517280005241b44852ff14bbf4d2384768c95c/index.js
// eslint-disable-next-line no-control-regex
const filenameReservedRegex = /[<>:"/\\|?*\u0000-\u001F]/g;

/**
 * Clean up a name for filename safety.
 */
export const filenamify = (str: string) =>
  slugify(str, {
    remove: filenameReservedRegex,
  });

/**
 * Parses an AWS document's object URL from the presigned url.
 * Example: https://s3.us-west-1.amazonaws.com/bucket/doc.pdf?AWSAccessKeyId=__&Content-Type=__ -> https://s3.us-west-1.amazonaws.com/bucket/doc.pdf
 */
export const getObjectUrlFromPresignedUrl = (
  url: string | undefined,
): string | undefined => {
  return url?.split('?')?.[0];
};

/**
 * Trims a string, returning null when nothing is left. For optional text a
 * form or API may send as blank.
 */
export const trimToNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};
